import { Dispatch, SetStateAction, useEffect } from 'react'
import { useAtom, useAtomValue } from 'jotai'
import isEmpty from 'lodash-es/isEmpty'
import { UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDebounce } from '@node-space/hooks'
import { SEARCH_INPUT_DEBOUNCE_TIME } from 'constants/General'
import { useWalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { BaseErrorResponse } from 'types/beneficiaries'
import { MappedErrorMessageState } from 'types/errors'
import { ConvertWallet, WalletType } from 'types/types'
import { floorWithPrecision } from 'utils/utils'
import { convertFromWalletAtom, convertToWalletAtom } from '../atoms/atoms'
import { ConvertDetailsForm, ConvertDetailsFormData } from '../schemas/convertDetailsSchema'

interface ConvertEffectsProps {
  convertFormValues: ConvertDetailsFormData
  exchangeRate: number
  formValues: ConvertDetailsForm
  convertFromCustomerWallet?: ConvertWallet
  sourceWallets: WalletType[]
  isErrorLimits: boolean
  ErrorLimits: Error
  setValue: UseFormSetValue<ConvertDetailsForm>
  setRequestError: Dispatch<SetStateAction<MappedErrorMessageState>>
  handleFetchQuoteEstimate: () => void
  enableV3Convert?: boolean
  isSameCurrency?: boolean
}

export const useConvertEffects = ({
  convertFormValues,
  exchangeRate,
  formValues,
  convertFromCustomerWallet,
  sourceWallets,
  isErrorLimits,
  ErrorLimits,
  setValue,
  setRequestError,
  handleFetchQuoteEstimate,
  enableV3Convert,
  isSameCurrency,
}: ConvertEffectsProps) => {
  const { t } = useTranslation()
  const [convertFromWallet, setConvertFromWallet] = useAtom(convertFromWalletAtom)
  const convertToWallet = useAtomValue(convertToWalletAtom)
  const [walletAction] = useWalletActionContext()

  useEffect(() => {
    if (convertFormValues?.changedField === 'toAmount') {
      setValue(
        'fromAmount',
        String(
          floorWithPrecision(
            Number(formValues?.toAmount) / exchangeRate,
            convertToWallet?.currency?.pricePrecision
          ) || ''
        )
      )
    }

    if (convertFormValues?.changedField === 'fromAmount') {
      setValue(
        'toAmount',
        String(
          floorWithPrecision(
            Number(formValues?.fromAmount) * exchangeRate,
            convertFromWallet?.currency?.pricePrecision
          ) || ''
        )
      )
    }
  }, [convertFormValues?.changedField, formValues?.fromAmount, formValues?.toAmount, exchangeRate])

  useEffect(() => {
    if (walletAction?.meta?.sourceWallet && sourceWallets && !convertFromWallet) {
      const sourceWallet = sourceWallets?.find(
        wallet => wallet?.lsid === walletAction?.meta?.sourceWallet?.lsid
      )

      if (!isEmpty(sourceWallet)) {
        setConvertFromWallet(sourceWallet)
      }
    }
  }, [walletAction, sourceWallets])

  useEffect(() => {
    if (convertFromCustomerWallet) {
      setConvertFromWallet(convertFromCustomerWallet)
      setValue('fromWallet', convertFromCustomerWallet?.lsid ?? '')
    }
  }, [convertFromCustomerWallet])

  useEffect(() => {
    if (convertToWallet) {
      setValue('toWallet', convertToWallet.lsid ?? '', {
        shouldValidate: true,
        shouldDirty: true,
      })
    } else {
      setValue('toWallet', '', {
        shouldValidate: false,
        shouldDirty: false,
      })
    }
  }, [convertToWallet])

  const editedAmount =
    convertFormValues?.changedField === 'toAmount' ? formValues?.toAmount : formValues?.fromAmount
  const debouncedEditedAmount = useDebounce(editedAmount, SEARCH_INPUT_DEBOUNCE_TIME)

  useEffect(() => {
    if (enableV3Convert) return
    if (formValues?.toWallet) {
      handleFetchQuoteEstimate()
    }
  }, [enableV3Convert, formValues?.toWallet])

  useEffect(() => {
    if (!enableV3Convert || isSameCurrency) return
    if (formValues?.toWallet && Number(debouncedEditedAmount) > 0) {
      handleFetchQuoteEstimate()
    }
  }, [enableV3Convert, isSameCurrency, formValues?.toWallet, debouncedEditedAmount])

  useEffect(() => {
    if (isErrorLimits) {
      const error: BaseErrorResponse = ErrorLimits

      setRequestError({
        handledMessage: t('wallets.convert.oopsSomethingWentWrongFetchLimits'),
        errorCode: error?.status,
        show: true,
      })
    }
  }, [isErrorLimits, ErrorLimits])
}
